import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sendFeedback, ApiError } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE THING YOU PRESS WHEN SOMETHING IS WRONG.
//
// Kyle, Jaxen and David are about to use this at once, and the channel between
// "this is broken" and a fix was a chat message to whoever happened to be in
// the room. That loses reports, and loses them SILENTLY — nobody can tell a bug
// nobody reported from a bug nobody fixed.
//
// ─────────────────────────────────────────────────────────────────────────────
// MINIMISED IS THE RESTING STATE, AND IT NEVER COVERS THE WORK.
//
// A feedback widget that occupies the screen gets closed once and never opened
// again. This is a small pill in the bottom corner: present on every page,
// costing nothing, one click from a form. It is the only floating element in
// the app, so it does not compete with anything.
//
// IT IS NAVY, NOT WARM. The brand rule is one warm CTA per view, and that one
// belongs to whatever the page is actually for. A permanently floating orange
// button would win that fight on every screen in the product.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE PAGE IS CAPTURED, NOT ASKED FOR.
//
// "Which screen were you on?" is the question every report needs and no human
// reliably answers — least of all afterwards, from a different window. The
// route and the browser go with the report automatically, so the two fields
// most useful for reproducing something arrive without anybody typing them.
//
// ONE REQUIRED FIELD. A validation error on a bug report is an insult:
// somebody stopped what they were doing to help and the product argued with
// them about the shape of it. A title is enough; triage can ask for the rest.
//
// AND IT SAYS WHAT HAPPENS NEXT. "Thanks for your feedback" is where reports go
// to die. This says the mechanism — queue, classified, Asana ticket — because
// the reason people stop reporting is believing it goes nowhere.
// ─────────────────────────────────────────────────────────────────────────────

type Kind = 'bug' | 'idea' | 'confusing';

const KINDS: { key: Kind; label: string; hint: string }[] = [
  { key: 'bug', label: 'Something is broken', hint: 'It did the wrong thing, or nothing.' },
  { key: 'idea', label: 'I want something', hint: 'It works, and it should do more.' },
  { key: 'confusing', label: 'This confused me', hint: 'It works and I could not tell.' },
];

export function Feedback() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Escape closes it. A floating panel with no keyboard exit is a trap for
  // anyone not using a mouse.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true); setError(null);
    try {
      await sendFeedback({
        kind,
        title: title.trim(),
        body: body.trim() || undefined,
        // Where they were standing, and what they were standing in.
        page: loc.pathname + loc.search,
        user_agent: navigator.userAgent,
      });
      setSent(true);
      setTitle(''); setBody('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button className="fbpill" onClick={() => { setOpen(true); setSent(false); }}
        title="Report a bug or ask for something">
        <span aria-hidden="true">✻</span> Feedback
      </button>
    );
  }

  return (
    <div className="fbpanel" role="dialog" aria-label="Send feedback">
      <header>
        <strong>{sent ? 'Filed' : 'What happened?'}</strong>
        <button className="fbclose" onClick={() => setOpen(false)} aria-label="Close">×</button>
      </header>

      {sent ? (
        <div className="fbbody">
          <p className="fieldhint">
            It goes into the build queue, gets classified as a bug or a request,
            and turns into an Asana ticket. Kyle sees the priority list every week.
          </p>
          <div className="actions">
            <button onClick={() => setSent(false)}>Report something else</button>
            <button className="mini" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      ) : (
        <form className="fbbody" onSubmit={(e) => void submit(e)}>
          <div className="fbkinds">
            {KINDS.map((k) => (
              <button type="button" key={k.key}
                className={`fbkind ${kind === k.key ? 'on' : ''}`}
                onClick={() => setKind(k.key)} title={k.hint}>
                {k.label}
              </button>
            ))}
          </div>

          <label className="field">
            <span className="field-label">In one line</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Arming a sequence returned a 409 and I could not tell why"
              autoFocus maxLength={200} />
          </label>

          <label className="field">
            <span className="field-label">Anything else <span className="meta">(optional)</span></span>
            <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="What you expected, what happened, anything you tried." />
          </label>

          {error && <p className="err">{error}</p>}

          <p className="fieldhint">
            The page you are on and your browser go with this automatically, so you
            do not have to describe where you were.
          </p>

          <div className="actions">
            <button type="submit" disabled={busy || !title.trim()}>
              {busy ? 'Sending…' : 'Send it'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
